// Code Snippet Library - Categorized code templates

export const CODE_SNIPPETS = {
  javascript: {
    label: 'JavaScript',
    icon: 'js',
    snippets: [
      {
        id: 'js-fetch',
        name: 'Fetch API Request',
        description: 'Make an HTTP request using fetch',
        code: `// Fetch API - GET request
async function fetchData(url) {
  try {
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(\`HTTP error! status: \${response.status}\`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

// Usage
fetchData('https://api.example.com/data')
  .then(data => console.log(data))
  .catch(err => console.error(err));`
      },
      {
        id: 'js-fetch-post',
        name: 'Fetch POST Request',
        description: 'Send data with POST request',
        code: `// Fetch API - POST request
async function postData(url, data) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  return response.json();
}

// Usage
postData('https://api.example.com/users', {
  name: 'John Doe',
  email: 'john@example.com'
}).then(data => console.log(data));`
      },
      {
        id: 'js-array-methods',
        name: 'Array Methods',
        description: 'Common array operations',
        code: `// Array Methods Examples
const numbers = [1, 2, 3, 4, 5];

// map - transform each element
const doubled = numbers.map(n => n * 2);
// [2, 4, 6, 8, 10]

// filter - keep elements that pass test
const evens = numbers.filter(n => n % 2 === 0);
// [2, 4]

// reduce - accumulate to single value
const sum = numbers.reduce((acc, n) => acc + n, 0);
// 15

// find - get first matching element
const firstEven = numbers.find(n => n % 2 === 0);
// 2

// some/every - test conditions
const hasEven = numbers.some(n => n % 2 === 0);  // true
const allEven = numbers.every(n => n % 2 === 0); // false`
      },
      {
        id: 'js-event-listener',
        name: 'Event Listener',
        description: 'Handle DOM events',
        code: `// Event Listener Examples

// Click event
document.getElementById('myButton').addEventListener('click', (e) => {
  console.log('Button clicked!', e.target);
});

// Form submit
document.querySelector('form').addEventListener('submit', (e) => {
  e.preventDefault(); // Prevent page reload
  const formData = new FormData(e.target);
  console.log('Form data:', Object.fromEntries(formData));
});

// Keyboard event
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    console.log('Escape pressed');
  }
});

// Remove listener
const handler = () => console.log('clicked');
element.addEventListener('click', handler);
element.removeEventListener('click', handler);`
      },
      {
        id: 'js-async-await',
        name: 'Async/Await Pattern',
        description: 'Handle asynchronous operations',
        code: `// Async/Await Examples

// Basic async function
async function getData() {
  const response = await fetch('/api/data');
  const data = await response.json();
  return data;
}

// Parallel requests
async function getMultiple() {
  const [users, posts] = await Promise.all([
    fetch('/api/users').then(r => r.json()),
    fetch('/api/posts').then(r => r.json())
  ]);
  return { users, posts };
}

// Error handling
async function safeFetch(url) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Request failed');
    return await response.json();
  } catch (error) {
    console.error('Error:', error.message);
    return null;
  }
}

// Sequential execution
async function processItems(items) {
  for (const item of items) {
    await processItem(item); // Wait for each
  }
}`
      },
      {
        id: 'js-debounce',
        name: 'Debounce Function',
        description: 'Limit function execution rate',
        code: `// Debounce - delay execution until pause in calls
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Usage - search input
const searchInput = document.getElementById('search');
const handleSearch = debounce((query) => {
  console.log('Searching for:', query);
  // API call here
}, 300);

searchInput.addEventListener('input', (e) => {
  handleSearch(e.target.value);
});`
      },
      {
        id: 'js-local-storage',
        name: 'LocalStorage Helper',
        description: 'Store and retrieve data locally',
        code: `// LocalStorage Helper Functions

const storage = {
  // Set item (handles objects)
  set(key, value) {
    localStorage.setItem(key, JSON.stringify(value));
  },
  
  // Get item (parses JSON)
  get(key, defaultValue = null) {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  },
  
  // Remove item
  remove(key) {
    localStorage.removeItem(key);
  },
  
  // Clear all
  clear() {
    localStorage.clear();
  }
};

// Usage
storage.set('user', { name: 'John', id: 123 });
const user = storage.get('user');
console.log(user.name); // 'John'`
      }
    ]
  },
  
  python: {
    label: 'Python',
    icon: 'py',
    snippets: [
      {
        id: 'py-api-request',
        name: 'API Request',
        description: 'Make HTTP requests with requests library',
        code: `# API Request with requests library
import requests

def fetch_data(url, headers=None):
    """Make a GET request and return JSON data."""
    try:
        response = requests.get(url, headers=headers)
        response.raise_for_status()  # Raise exception for bad status
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f"Request failed: {e}")
        return None

def post_data(url, data, headers=None):
    """Make a POST request with JSON data."""
    headers = headers or {'Content-Type': 'application/json'}
    response = requests.post(url, json=data, headers=headers)
    return response.json()

# Usage
data = fetch_data('https://api.example.com/users')
result = post_data('https://api.example.com/users', {'name': 'John'})`
      },
      {
        id: 'py-file-io',
        name: 'File I/O',
        description: 'Read and write files',
        code: `# File I/O Operations

# Read entire file
with open('file.txt', 'r') as f:
    content = f.read()

# Read lines
with open('file.txt', 'r') as f:
    lines = f.readlines()

# Write to file
with open('output.txt', 'w') as f:
    f.write('Hello, World!')

# Append to file
with open('log.txt', 'a') as f:
    f.write('New log entry\\n')

# Read JSON
import json
with open('data.json', 'r') as f:
    data = json.load(f)

# Write JSON
with open('data.json', 'w') as f:
    json.dump({'key': 'value'}, f, indent=2)

# CSV handling
import csv
with open('data.csv', 'r') as f:
    reader = csv.DictReader(f)
    for row in reader:
        print(row)`
      },
      {
        id: 'py-class',
        name: 'Class Template',
        description: 'Python class with common patterns',
        code: `# Python Class Template
from dataclasses import dataclass
from typing import Optional, List

@dataclass
class User:
    """User class with dataclass decorator."""
    id: int
    name: str
    email: str
    active: bool = True
    
    def __post_init__(self):
        """Validate after initialization."""
        if '@' not in self.email:
            raise ValueError('Invalid email')

# Traditional class
class Product:
    """Product class with traditional approach."""
    
    def __init__(self, name: str, price: float):
        self.name = name
        self.price = price
        self._discount = 0
    
    @property
    def discount(self) -> float:
        return self._discount
    
    @discount.setter
    def discount(self, value: float):
        if 0 <= value <= 100:
            self._discount = value
    
    def get_final_price(self) -> float:
        return self.price * (1 - self._discount / 100)
    
    def __repr__(self):
        return f"Product('{self.name}', {self.price})"`
      },
      {
        id: 'py-list-comprehension',
        name: 'List Comprehension',
        description: 'Concise list operations',
        code: `# List Comprehension Examples

numbers = [1, 2, 3, 4, 5]

# Basic - square each number
squares = [x**2 for x in numbers]
# [1, 4, 9, 16, 25]

# With condition - even numbers only
evens = [x for x in numbers if x % 2 == 0]
# [2, 4]

# Transform with condition
result = [x**2 if x % 2 == 0 else x for x in numbers]
# [1, 4, 3, 16, 5]

# Nested - flatten 2D list
matrix = [[1, 2], [3, 4], [5, 6]]
flat = [x for row in matrix for x in row]
# [1, 2, 3, 4, 5, 6]

# Dictionary comprehension
names = ['alice', 'bob', 'charlie']
name_lengths = {name: len(name) for name in names}
# {'alice': 5, 'bob': 3, 'charlie': 7}

# Set comprehension
unique_lengths = {len(name) for name in names}
# {3, 5, 7}`
      },
      {
        id: 'py-decorator',
        name: 'Decorator Pattern',
        description: 'Function decorators',
        code: `# Python Decorators
import functools
import time

# Basic decorator
def log_calls(func):
    @functools.wraps(func)
    def wrapper(*args, **kwargs):
        print(f"Calling {func.__name__}")
        result = func(*args, **kwargs)
        print(f"Finished {func.__name__}")
        return result
    return wrapper

# Timer decorator
def timer(func):
    @functools.wraps(func)
    def wrapper(*args, **kwargs):
        start = time.time()
        result = func(*args, **kwargs)
        end = time.time()
        print(f"{func.__name__} took {end - start:.2f}s")
        return result
    return wrapper

# Decorator with arguments
def retry(max_attempts=3):
    def decorator(func):
        @functools.wraps(func)
        def wrapper(*args, **kwargs):
            for attempt in range(max_attempts):
                try:
                    return func(*args, **kwargs)
                except Exception as e:
                    if attempt == max_attempts - 1:
                        raise
                    print(f"Attempt {attempt + 1} failed: {e}")
        return wrapper
    return decorator

# Usage
@timer
@log_calls
def slow_function():
    time.sleep(1)
    return "Done"`
      },
      {
        id: 'py-context-manager',
        name: 'Context Manager',
        description: 'Custom context manager',
        code: `# Context Manager Examples
from contextlib import contextmanager
import time

# Class-based context manager
class Timer:
    def __enter__(self):
        self.start = time.time()
        return self
    
    def __exit__(self, *args):
        self.elapsed = time.time() - self.start
        print(f"Elapsed: {self.elapsed:.2f}s")

# Function-based with decorator
@contextmanager
def timer():
    start = time.time()
    yield
    elapsed = time.time() - start
    print(f"Elapsed: {elapsed:.2f}s")

# Database connection example
@contextmanager
def db_connection(host):
    conn = create_connection(host)
    try:
        yield conn
    finally:
        conn.close()

# Usage
with Timer() as t:
    # do something
    pass

with timer():
    # do something
    pass`
      }
    ]
  },
  
  react: {
    label: 'React',
    icon: 'jsx',
    snippets: [
      {
        id: 'react-component',
        name: 'Functional Component',
        description: 'Basic React component template',
        code: `// React Functional Component
import React from 'react';

interface Props {
  title: string;
  description?: string;
  onAction?: () => void;
}

const MyComponent: React.FC<Props> = ({ 
  title, 
  description = 'Default description',
  onAction 
}) => {
  return (
    <div className="my-component">
      <h2>{title}</h2>
      <p>{description}</p>
      {onAction && (
        <button onClick={onAction}>
          Click Me
        </button>
      )}
    </div>
  );
};

export default MyComponent;`
      },
      {
        id: 'react-usestate',
        name: 'useState Hook',
        description: 'State management with useState',
        code: `// useState Hook Examples
import React, { useState } from 'react';

function Counter() {
  // Simple state
  const [count, setCount] = useState(0);
  
  // Object state
  const [user, setUser] = useState({ name: '', email: '' });
  
  // Array state
  const [items, setItems] = useState([]);
  
  // Update handlers
  const increment = () => setCount(prev => prev + 1);
  
  const updateUser = (field, value) => {
    setUser(prev => ({ ...prev, [field]: value }));
  };
  
  const addItem = (item) => {
    setItems(prev => [...prev, item]);
  };
  
  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={increment}>+1</button>
      
      <input 
        value={user.name}
        onChange={(e) => updateUser('name', e.target.value)}
        placeholder="Name"
      />
    </div>
  );
}`
      },
      {
        id: 'react-useeffect',
        name: 'useEffect Hook',
        description: 'Side effects with useEffect',
        code: `// useEffect Hook Examples
import React, { useState, useEffect } from 'react';

function DataFetcher({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch on mount and when userId changes
  useEffect(() => {
    let cancelled = false;
    
    async function fetchData() {
      setLoading(true);
      try {
        const response = await fetch(\`/api/users/\${userId}\`);
        const json = await response.json();
        if (!cancelled) {
          setData(json);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    
    fetchData();
    
    // Cleanup function
    return () => {
      cancelled = true;
    };
  }, [userId]); // Dependency array

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;
  return <div>{JSON.stringify(data)}</div>;
}`
      },
      {
        id: 'react-custom-hook',
        name: 'Custom Hook',
        description: 'Create reusable logic with custom hooks',
        code: `// Custom Hook - useLocalStorage
import { useState, useEffect } from 'react';

function useLocalStorage<T>(key: string, initialValue: T) {
  // Get stored value or use initial
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  // Update localStorage when value changes
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [key, storedValue]);

  return [storedValue, setStoredValue] as const;
}

// Usage
function Settings() {
  const [theme, setTheme] = useLocalStorage('theme', 'light');
  const [user, setUser] = useLocalStorage('user', null);

  return (
    <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
      Toggle Theme ({theme})
    </button>
  );
}`
      },
      {
        id: 'react-context',
        name: 'Context API',
        description: 'Global state with Context',
        code: `// React Context API
import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define types
interface User {
  id: string;
  name: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const login = (userData: User) => setUser(userData);
  const logout = () => setUser(null);

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Usage in component
function Profile() {
  const { user, logout, isAuthenticated } = useAuth();
  
  if (!isAuthenticated) return <p>Please log in</p>;
  return <p>Welcome, {user?.name}!</p>;
}`
      },
      {
        id: 'react-form',
        name: 'Form Handling',
        description: 'Form with validation',
        code: `// React Form with Validation
import React, { useState, FormEvent } from 'react';

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.includes('@')) {
      newErrors.email = 'Valid email is required';
    }
    if (formData.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setSubmitting(true);
    try {
      await fetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      alert('Submitted!');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <input
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        placeholder="Name"
      />
      {errors.name && <span className="error">{errors.name}</span>}
      
      <button type="submit" disabled={submitting}>
        {submitting ? 'Sending...' : 'Submit'}
      </button>
    </form>
  );
}`
      }
    ]
  },
  
  html: {
    label: 'HTML',
    icon: 'html',
    snippets: [
      {
        id: 'html-form',
        name: 'Form Template',
        description: 'Accessible form with validation',
        code: `<!-- Accessible Form Template -->
<form action="/submit" method="POST" class="form">
  <div class="form-group">
    <label for="name">Full Name *</label>
    <input 
      type="text" 
      id="name" 
      name="name" 
      required
      autocomplete="name"
      placeholder="John Doe"
    />
  </div>

  <div class="form-group">
    <label for="email">Email Address *</label>
    <input 
      type="email" 
      id="email" 
      name="email" 
      required
      autocomplete="email"
      placeholder="john@example.com"
    />
  </div>

  <div class="form-group">
    <label for="phone">Phone (optional)</label>
    <input 
      type="tel" 
      id="phone" 
      name="phone"
      autocomplete="tel"
      pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
      placeholder="123-456-7890"
    />
  </div>

  <div class="form-group">
    <label for="message">Message *</label>
    <textarea 
      id="message" 
      name="message" 
      rows="4"
      required
      placeholder="Your message..."
    ></textarea>
  </div>

  <button type="submit">Send Message</button>
</form>`
      },
      {
        id: 'html-card',
        name: 'Card Component',
        description: 'Reusable card layout',
        code: `<!-- Card Component -->
<article class="card">
  <img 
    src="/image.jpg" 
    alt="Card image description"
    class="card-image"
    loading="lazy"
  />
  
  <div class="card-content">
    <span class="card-badge">Featured</span>
    
    <h3 class="card-title">Card Title</h3>
    
    <p class="card-description">
      Brief description of the card content goes here. 
      Keep it concise and informative.
    </p>
    
    <div class="card-meta">
      <span class="card-date">Jan 15, 2025</span>
      <span class="card-author">By John Doe</span>
    </div>
    
    <div class="card-actions">
      <a href="/read-more" class="btn btn-primary">Read More</a>
      <button class="btn btn-secondary">Save</button>
    </div>
  </div>
</article>`
      },
      {
        id: 'html-nav',
        name: 'Navigation',
        description: 'Responsive navigation bar',
        code: `<!-- Responsive Navigation -->
<header class="header">
  <nav class="nav" aria-label="Main navigation">
    <a href="/" class="nav-logo">
      <img src="/logo.svg" alt="Company Name" />
    </a>
    
    <button 
      class="nav-toggle" 
      aria-expanded="false"
      aria-controls="nav-menu"
      aria-label="Toggle navigation"
    >
      <span class="hamburger"></span>
    </button>
    
    <ul id="nav-menu" class="nav-menu">
      <li><a href="/" class="nav-link active">Home</a></li>
      <li><a href="/about" class="nav-link">About</a></li>
      <li><a href="/services" class="nav-link">Services</a></li>
      <li class="nav-dropdown">
        <button class="nav-link" aria-expanded="false">
          Products <span aria-hidden="true">▼</span>
        </button>
        <ul class="dropdown-menu">
          <li><a href="/products/a">Product A</a></li>
          <li><a href="/products/b">Product B</a></li>
          <li><a href="/products/c">Product C</a></li>
        </ul>
      </li>
      <li><a href="/contact" class="nav-link">Contact</a></li>
    </ul>
    
    <div class="nav-actions">
      <a href="/login" class="btn btn-outline">Login</a>
      <a href="/signup" class="btn btn-primary">Sign Up</a>
    </div>
  </nav>
</header>`
      },
      {
        id: 'html-modal',
        name: 'Modal Dialog',
        description: 'Accessible modal/dialog',
        code: `<!-- Accessible Modal Dialog -->
<div 
  id="modal" 
  class="modal" 
  role="dialog" 
  aria-modal="true"
  aria-labelledby="modal-title"
  aria-describedby="modal-description"
  hidden
>
  <div class="modal-backdrop" data-close-modal></div>
  
  <div class="modal-content">
    <header class="modal-header">
      <h2 id="modal-title">Modal Title</h2>
      <button 
        class="modal-close" 
        data-close-modal
        aria-label="Close modal"
      >
        &times;
      </button>
    </header>
    
    <div class="modal-body">
      <p id="modal-description">
        Modal content goes here. This could be a form,
        confirmation message, or any other content.
      </p>
    </div>
    
    <footer class="modal-footer">
      <button class="btn btn-secondary" data-close-modal>
        Cancel
      </button>
      <button class="btn btn-primary">
        Confirm
      </button>
    </footer>
  </div>
</div>

<script>
// Toggle modal
const openModal = () => document.getElementById('modal').hidden = false;
const closeModal = () => document.getElementById('modal').hidden = true;

// Close on backdrop click or close button
document.querySelectorAll('[data-close-modal]').forEach(el => {
  el.addEventListener('click', closeModal);
});

// Close on Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeModal();
});
</script>`
      },
      {
        id: 'html-table',
        name: 'Data Table',
        description: 'Accessible responsive table',
        code: `<!-- Accessible Data Table -->
<div class="table-container">
  <table class="data-table">
    <caption>Monthly Sales Report</caption>
    
    <thead>
      <tr>
        <th scope="col">Product</th>
        <th scope="col">Category</th>
        <th scope="col" class="numeric">Price</th>
        <th scope="col" class="numeric">Quantity</th>
        <th scope="col" class="numeric">Total</th>
        <th scope="col">Actions</th>
      </tr>
    </thead>
    
    <tbody>
      <tr>
        <td data-label="Product">Widget Pro</td>
        <td data-label="Category">Electronics</td>
        <td data-label="Price" class="numeric">$99.00</td>
        <td data-label="Quantity" class="numeric">25</td>
        <td data-label="Total" class="numeric">$2,475.00</td>
        <td data-label="Actions">
          <button class="btn-icon" aria-label="Edit">✏️</button>
          <button class="btn-icon" aria-label="Delete">🗑️</button>
        </td>
      </tr>
      <tr>
        <td data-label="Product">Gadget Plus</td>
        <td data-label="Category">Accessories</td>
        <td data-label="Price" class="numeric">$49.00</td>
        <td data-label="Quantity" class="numeric">50</td>
        <td data-label="Total" class="numeric">$2,450.00</td>
        <td data-label="Actions">
          <button class="btn-icon" aria-label="Edit">✏️</button>
          <button class="btn-icon" aria-label="Delete">🗑️</button>
        </td>
      </tr>
    </tbody>
    
    <tfoot>
      <tr>
        <th scope="row" colspan="4">Grand Total</th>
        <td class="numeric"><strong>$4,925.00</strong></td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</div>`
      }
    ]
  },
  
  css: {
    label: 'CSS',
    icon: 'css',
    snippets: [
      {
        id: 'css-flexbox',
        name: 'Flexbox Layout',
        description: 'Common flexbox patterns',
        code: `/* Flexbox Layout Patterns */

/* Center everything */
.center-all {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Space between items */
.space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Vertical stack */
.stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Responsive wrap */
.wrap-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.wrap-container > * {
  flex: 1 1 300px; /* Grow, shrink, min-width */
}

/* Sidebar layout */
.sidebar-layout {
  display: flex;
  gap: 2rem;
}

.sidebar {
  flex: 0 0 250px; /* Fixed width */
}

.main-content {
  flex: 1; /* Fill remaining space */
}

/* Holy grail layout */
.holy-grail {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.holy-grail main {
  flex: 1;
}`
      },
      {
        id: 'css-grid',
        name: 'CSS Grid Layout',
        description: 'Grid layout patterns',
        code: `/* CSS Grid Layout Patterns */

/* Basic grid */
.grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

/* Responsive auto-fit */
.auto-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

/* Dashboard layout */
.dashboard {
  display: grid;
  grid-template-columns: 250px 1fr;
  grid-template-rows: 60px 1fr;
  grid-template-areas:
    "sidebar header"
    "sidebar main";
  min-height: 100vh;
}

.dashboard-header { grid-area: header; }
.dashboard-sidebar { grid-area: sidebar; }
.dashboard-main { grid-area: main; }

/* Card grid with featured item */
.featured-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

.featured-grid .featured {
  grid-column: span 2;
  grid-row: span 2;
}

/* Masonry-like effect */
.masonry {
  columns: 3 300px;
  column-gap: 1rem;
}

.masonry > * {
  break-inside: avoid;
  margin-bottom: 1rem;
}`
      },
      {
        id: 'css-animations',
        name: 'CSS Animations',
        description: 'Common animation patterns',
        code: `/* CSS Animation Patterns */

/* Fade in */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

/* Slide up */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-up {
  animation: slideUp 0.4s ease-out;
}

/* Pulse effect */
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.pulse:hover {
  animation: pulse 0.5s ease-in-out infinite;
}

/* Skeleton loading */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.skeleton {
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Spin */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}`
      },
      {
        id: 'css-buttons',
        name: 'Button Styles',
        description: 'Button component styles',
        code: `/* Button Styles */

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.5;
  border-radius: 0.375rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

/* Primary */
.btn-primary {
  background-color: #4f46e5;
  color: white;
}

.btn-primary:hover {
  background-color: #4338ca;
}

/* Secondary */
.btn-secondary {
  background-color: #6b7280;
  color: white;
}

/* Outline */
.btn-outline {
  background-color: transparent;
  border-color: #d1d5db;
  color: #374151;
}

.btn-outline:hover {
  background-color: #f3f4f6;
}

/* Ghost */
.btn-ghost {
  background-color: transparent;
  color: #4f46e5;
}

.btn-ghost:hover {
  background-color: #eef2ff;
}

/* Sizes */
.btn-sm { padding: 0.375rem 0.75rem; font-size: 0.75rem; }
.btn-lg { padding: 0.75rem 1.5rem; font-size: 1rem; }

/* Disabled */
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading state */
.btn-loading {
  position: relative;
  color: transparent;
}

.btn-loading::after {
  content: "";
  position: absolute;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}`
      },
      {
        id: 'css-variables',
        name: 'CSS Variables',
        description: 'Design system with CSS custom properties',
        code: `/* CSS Custom Properties - Design System */

:root {
  /* Colors */
  --color-primary: #4f46e5;
  --color-primary-dark: #4338ca;
  --color-secondary: #6b7280;
  --color-success: #10b981;
  --color-warning: #f59e0b;
  --color-error: #ef4444;
  
  /* Neutrals */
  --color-gray-50: #f9fafb;
  --color-gray-100: #f3f4f6;
  --color-gray-200: #e5e7eb;
  --color-gray-500: #6b7280;
  --color-gray-900: #111827;
  
  /* Typography */
  --font-sans: 'Inter', system-ui, sans-serif;
  --font-mono: 'Fira Code', monospace;
  
  /* Font sizes */
  --text-xs: 0.75rem;
  --text-sm: 0.875rem;
  --text-base: 1rem;
  --text-lg: 1.125rem;
  --text-xl: 1.25rem;
  --text-2xl: 1.5rem;
  
  /* Spacing */
  --space-1: 0.25rem;
  --space-2: 0.5rem;
  --space-3: 0.75rem;
  --space-4: 1rem;
  --space-6: 1.5rem;
  --space-8: 2rem;
  
  /* Border radius */
  --radius-sm: 0.25rem;
  --radius-md: 0.375rem;
  --radius-lg: 0.5rem;
  --radius-full: 9999px;
  
  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 10px 15px rgba(0, 0, 0, 0.1);
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  :root {
    --color-gray-50: #18181b;
    --color-gray-100: #27272a;
    --color-gray-900: #fafafa;
  }
}`
      }
    ]
  },
  
  sql: {
    label: 'SQL',
    icon: 'sql',
    snippets: [
      {
        id: 'sql-select',
        name: 'SELECT Queries',
        description: 'Common SELECT patterns',
        code: `-- SELECT Query Examples

-- Basic select
SELECT * FROM users WHERE active = true;

-- Select specific columns
SELECT id, name, email 
FROM users 
WHERE created_at > '2024-01-01';

-- With JOIN
SELECT 
    u.name,
    u.email,
    o.order_id,
    o.total
FROM users u
INNER JOIN orders o ON u.id = o.user_id
WHERE o.status = 'completed';

-- With aggregation
SELECT 
    category,
    COUNT(*) as total_products,
    AVG(price) as avg_price,
    SUM(quantity) as total_stock
FROM products
GROUP BY category
HAVING COUNT(*) > 5
ORDER BY total_products DESC;

-- Subquery
SELECT * FROM users
WHERE id IN (
    SELECT user_id 
    FROM orders 
    WHERE total > 100
);

-- CTE (Common Table Expression)
WITH active_users AS (
    SELECT user_id, COUNT(*) as order_count
    FROM orders
    WHERE created_at > NOW() - INTERVAL '30 days'
    GROUP BY user_id
)
SELECT u.*, au.order_count
FROM users u
JOIN active_users au ON u.id = au.user_id;`
      },
      {
        id: 'sql-crud',
        name: 'CRUD Operations',
        description: 'Insert, Update, Delete examples',
        code: `-- CRUD Operations

-- INSERT single row
INSERT INTO users (name, email, created_at)
VALUES ('John Doe', 'john@example.com', NOW());

-- INSERT multiple rows
INSERT INTO products (name, price, category) VALUES
    ('Widget A', 29.99, 'Electronics'),
    ('Widget B', 39.99, 'Electronics'),
    ('Gadget C', 19.99, 'Accessories');

-- INSERT from SELECT
INSERT INTO archive_orders (order_id, user_id, total)
SELECT order_id, user_id, total
FROM orders
WHERE created_at < '2024-01-01';

-- UPDATE
UPDATE users 
SET 
    name = 'Jane Doe',
    updated_at = NOW()
WHERE id = 1;

-- UPDATE with JOIN
UPDATE products p
SET price = p.price * 1.1
FROM categories c
WHERE p.category_id = c.id
AND c.name = 'Electronics';

-- DELETE
DELETE FROM users WHERE id = 1;

-- DELETE with subquery
DELETE FROM orders
WHERE user_id IN (
    SELECT id FROM users WHERE active = false
);

-- UPSERT (PostgreSQL)
INSERT INTO users (email, name)
VALUES ('john@example.com', 'John')
ON CONFLICT (email) 
DO UPDATE SET name = EXCLUDED.name;`
      },
      {
        id: 'sql-create-table',
        name: 'CREATE TABLE',
        description: 'Table creation with constraints',
        code: `-- CREATE TABLE Examples

-- Users table
CREATE TABLE users (
    id SERIAL PRIMARY KEY,
    email VARCHAR(255) UNIQUE NOT NULL,
    name VARCHAR(100) NOT NULL,
    password_hash VARCHAR(255) NOT NULL,
    role VARCHAR(20) DEFAULT 'user',
    active BOOLEAN DEFAULT true,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Orders table with foreign key
CREATE TABLE orders (
    id SERIAL PRIMARY KEY,
    user_id INTEGER NOT NULL REFERENCES users(id),
    status VARCHAR(20) DEFAULT 'pending',
    total DECIMAL(10, 2) NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    
    CONSTRAINT valid_status CHECK (
        status IN ('pending', 'processing', 'completed', 'cancelled')
    )
);

-- Order items (many-to-many)
CREATE TABLE order_items (
    id SERIAL PRIMARY KEY,
    order_id INTEGER REFERENCES orders(id) ON DELETE CASCADE,
    product_id INTEGER REFERENCES products(id),
    quantity INTEGER NOT NULL CHECK (quantity > 0),
    price DECIMAL(10, 2) NOT NULL,
    
    UNIQUE(order_id, product_id)
);

-- Create indexes
CREATE INDEX idx_users_email ON users(email);
CREATE INDEX idx_orders_user_id ON orders(user_id);
CREATE INDEX idx_orders_created ON orders(created_at DESC);`
      }
    ]
  },
  
  bash: {
    label: 'Bash/Shell',
    icon: 'sh',
    snippets: [
      {
        id: 'bash-basics',
        name: 'Bash Basics',
        description: 'Variables, loops, conditions',
        code: `#!/bin/bash

# Variables
NAME="John"
COUNT=10
FILES=$(ls -la)

# Print
echo "Hello, $NAME"
echo "Count: $COUNT"

# Conditionals
if [ "$NAME" == "John" ]; then
    echo "Name is John"
elif [ "$NAME" == "Jane" ]; then
    echo "Name is Jane"
else
    echo "Unknown name"
fi

# File checks
if [ -f "file.txt" ]; then
    echo "File exists"
fi

if [ -d "directory" ]; then
    echo "Directory exists"
fi

# Loops
for i in {1..5}; do
    echo "Number: $i"
done

for file in *.txt; do
    echo "Processing: $file"
done

while read -r line; do
    echo "Line: $line"
done < input.txt

# Functions
greet() {
    local name=$1
    echo "Hello, $name!"
}

greet "World"`
      },
      {
        id: 'bash-file-ops',
        name: 'File Operations',
        description: 'Common file operations',
        code: `#!/bin/bash

# Create directory structure
mkdir -p project/{src,tests,docs}

# Copy files
cp source.txt destination.txt
cp -r source_dir/ dest_dir/

# Move/rename
mv old_name.txt new_name.txt

# Delete
rm file.txt
rm -rf directory/

# Find files
find . -name "*.txt"
find . -type f -mtime -7  # Modified in last 7 days
find . -size +10M         # Larger than 10MB

# Search in files
grep "pattern" file.txt
grep -r "pattern" ./      # Recursive
grep -l "pattern" *.txt   # List files only

# Replace in files
sed -i 's/old/new/g' file.txt

# File permissions
chmod 755 script.sh       # rwxr-xr-x
chmod +x script.sh        # Add execute
chown user:group file.txt

# Archive
tar -czvf archive.tar.gz directory/
tar -xzvf archive.tar.gz

# Disk usage
du -sh directory/         # Directory size
df -h                     # Disk free space`
      },
      {
        id: 'bash-git',
        name: 'Git Commands',
        description: 'Common Git operations',
        code: `#!/bin/bash

# Configuration
git config --global user.name "Your Name"
git config --global user.email "you@example.com"

# Initialize & Clone
git init
git clone https://github.com/user/repo.git

# Status & Diff
git status
git diff
git diff --staged

# Staging & Committing
git add .
git add -p                    # Interactive staging
git commit -m "Message"
git commit --amend            # Modify last commit

# Branches
git branch                    # List branches
git branch feature-name       # Create branch
git checkout feature-name     # Switch branch
git checkout -b feature-name  # Create & switch
git merge feature-name        # Merge branch
git branch -d feature-name    # Delete branch

# Remote
git remote -v
git fetch origin
git pull origin main
git push origin main
git push -u origin feature    # Set upstream

# Stash
git stash
git stash list
git stash pop
git stash drop

# History
git log --oneline
git log --graph --all
git show commit_hash

# Undo
git checkout -- file.txt      # Discard changes
git reset HEAD file.txt       # Unstage
git reset --soft HEAD~1       # Undo commit, keep changes
git reset --hard HEAD~1       # Undo commit, discard changes
git revert commit_hash        # Create undo commit`
      }
    ]
  },
  
  api: {
    label: 'API Examples',
    icon: 'api',
    snippets: [
      {
        id: 'api-rest',
        name: 'REST API Endpoints',
        description: 'RESTful API design patterns',
        code: `// REST API Endpoint Examples

// GET - List resources
// GET /api/users
// GET /api/users?page=1&limit=10&sort=name

// GET - Single resource
// GET /api/users/123

// POST - Create resource
// POST /api/users
// Body: { "name": "John", "email": "john@example.com" }

// PUT - Full update
// PUT /api/users/123
// Body: { "name": "John Doe", "email": "john@example.com" }

// PATCH - Partial update
// PATCH /api/users/123
// Body: { "name": "John Doe" }

// DELETE - Remove resource
// DELETE /api/users/123

// Nested resources
// GET /api/users/123/orders
// POST /api/users/123/orders

// Filtering & Search
// GET /api/products?category=electronics&min_price=100
// GET /api/users?search=john

// Response format
{
  "success": true,
  "data": {
    "id": 123,
    "name": "John Doe"
  },
  "meta": {
    "page": 1,
    "total": 100
  }
}

// Error response
{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "Email is required",
    "details": [
      { "field": "email", "message": "Required" }
    ]
  }
}`
      },
      {
        id: 'api-curl',
        name: 'cURL Commands',
        description: 'API testing with cURL',
        code: `# cURL API Testing Examples

# GET request
curl https://api.example.com/users

# GET with headers
curl -H "Authorization: Bearer TOKEN" \\
     -H "Accept: application/json" \\
     https://api.example.com/users

# POST with JSON body
curl -X POST \\
     -H "Content-Type: application/json" \\
     -d '{"name": "John", "email": "john@example.com"}' \\
     https://api.example.com/users

# POST form data
curl -X POST \\
     -F "name=John" \\
     -F "file=@photo.jpg" \\
     https://api.example.com/upload

# PUT request
curl -X PUT \\
     -H "Content-Type: application/json" \\
     -d '{"name": "John Doe"}' \\
     https://api.example.com/users/123

# DELETE request
curl -X DELETE \\
     -H "Authorization: Bearer TOKEN" \\
     https://api.example.com/users/123

# Show response headers
curl -I https://api.example.com/users

# Verbose output (debugging)
curl -v https://api.example.com/users

# Save response to file
curl -o response.json https://api.example.com/users

# Follow redirects
curl -L https://example.com/redirect`
      },
      {
        id: 'api-express',
        name: 'Express.js API',
        description: 'Node.js Express API setup',
        code: `// Express.js REST API
const express = require('express');
const app = express();

// Middleware
app.use(express.json());

// In-memory data (replace with database)
let users = [];

// GET all users
app.get('/api/users', (req, res) => {
  const { page = 1, limit = 10 } = req.query;
  const start = (page - 1) * limit;
  const paginatedUsers = users.slice(start, start + limit);
  
  res.json({
    data: paginatedUsers,
    meta: { page, limit, total: users.length }
  });
});

// GET single user
app.get('/api/users/:id', (req, res) => {
  const user = users.find(u => u.id === req.params.id);
  if (!user) {
    return res.status(404).json({ error: 'User not found' });
  }
  res.json(user);
});

// POST create user
app.post('/api/users', (req, res) => {
  const { name, email } = req.body;
  
  if (!name || !email) {
    return res.status(400).json({ error: 'Name and email required' });
  }
  
  const newUser = {
    id: Date.now().toString(),
    name,
    email,
    createdAt: new Date()
  };
  
  users.push(newUser);
  res.status(201).json(newUser);
});

// PUT update user
app.put('/api/users/:id', (req, res) => {
  const index = users.findIndex(u => u.id === req.params.id);
  if (index === -1) {
    return res.status(404).json({ error: 'User not found' });
  }
  
  users[index] = { ...users[index], ...req.body };
  res.json(users[index]);
});

// DELETE user
app.delete('/api/users/:id', (req, res) => {
  users = users.filter(u => u.id !== req.params.id);
  res.status(204).send();
});

app.listen(3000, () => console.log('Server running on port 3000'));`
      }
    ]
  }
};

export const getAllSnippets = () => {
  const all = [];
  Object.entries(CODE_SNIPPETS).forEach(([categoryKey, category]) => {
    category.snippets.forEach(snippet => {
      all.push({
        ...snippet,
        category: categoryKey,
        categoryLabel: category.label
      });
    });
  });
  return all;
};

export const searchSnippets = (query) => {
  const all = getAllSnippets();
  const lowerQuery = query.toLowerCase();
  return all.filter(snippet => 
    snippet.name.toLowerCase().includes(lowerQuery) ||
    snippet.description.toLowerCase().includes(lowerQuery) ||
    snippet.categoryLabel.toLowerCase().includes(lowerQuery)
  );
};
